import React, { useEffect, useState, useCallback } from "react";
import { fdelete, fget } from "../../API/callsAPI";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import {
  CaretRight,
  Paperclip,
  Pencil,
  Plus,
  Share,
  Trash,
} from "phosphor-react";
import { DataGrid } from "@mui/x-data-grid";
import CustomPageEntryOrEditRecord from "./CustomPageEntryOrEditRecord";
import commonStyles from "../../Utils/commonStyles";
import NoRowsSvg from "./NoRowsSvg";
import { Link } from "react-router-dom";
import DeleteModal from "../Utilities/DeleteModal/DeleteModal.component";
import Loader from "../Utilities/Loader/Loader";
import ErrorOrSuccessMessage from "../Utilities/ErrorOrSuccessMessage/ErrorOrSuccessMessage.component";
import EditCustomPageModal from "./EditCustomPage";
import MediaDrawer from "../Utilities/MediaDrawer/MediaDrawer";
import { CheckSquare } from "react-feather";

const CustomPageRendererOverview = ({ params }) => {
  const commonClasses = commonStyles();
  const [isCreateRecordModalOpen, setIsCreateRecordModalOpen] = useState(false);

  // Modal/Drawer States
  const [isEditPageDrawerOpen, setIsEditPageDrawerOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const [editRecordModalData, setEditRecordModalData] = useState({
    isOpen: false,
    data: null,
  });
  const [deleteRecordModalData, setDeleteRecordModalData] = useState({
    isOpen: false,
    data: null,
  });
  const [snackValue, setSnackValue] = useState({
    isOpen: false,
    message: "",
    isError: false,
  });

  const [isMultiSelectEnabled, setIsMultiSelectEnabled] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState(null);

  const [fields, setFields] = useState([]);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [pageName, setPageName] = useState("");
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMediaDrawerOpen, setIsMediaDrawerOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const renderCellValue = (cellParams) => {
    if (!cellParams) {
      return <Typography>-</Typography>;
    }

    console.log(cellParams);
    const value = cellParams.value;
    if (value === null || value === undefined) {
      return <Typography>-</Typography>;
    }
    if (
      cellParams.field === "created_at" ||
      cellParams.field === "updated_at"
    ) {
      return <Typography>{new Date(value).toLocaleString()}</Typography>;
    }
    if (cellParams.field === "allowed_dept") {
      return (
        <Typography>{value.map((item) => item.name).join(", ")}</Typography>
      );
    }
    switch (typeof value) {
      case "string" || "number" | "boolean":
        return <Typography>{value}</Typography>;
      case "object":
        return (
          <Typography>
            {value.name ||
              `${value.first_name} ${value.last_name}` ||
              value.id ||
              JSON.stringify(value)}
          </Typography>
        );
      case "array":
        return <Typography>{value.join(", ")}</Typography>;
      default:
        return <Typography>{JSON.stringify(value)}</Typography>;
    }
  };

  const handleToggleMultiSelect = () => {
    setIsMultiSelectEnabled(!isMultiSelectEnabled);
    setSelectedRows(new Set());
    setLastClickedIndex(null);
  };

  const handleRowSelection = (rowId, index, event) => {
    setSelectedRows((prevSelectedRows) => {
      const newSelectedRows = new Set(prevSelectedRows);

      if (event.shiftKey && lastClickedIndex !== null) {
        const start = Math.min(lastClickedIndex, index);
        const end = Math.max(lastClickedIndex, index);

        for (let i = start; i <= end; i++) {
          if (rows[i]) {
            newSelectedRows.add(rows[i].id);
          }
        }
      } else {
        if (newSelectedRows.has(rowId)) {
          newSelectedRows.delete(rowId);
        } else {
          newSelectedRows.add(rowId);
        }
      }

      return newSelectedRows;
    });
    setLastClickedIndex(index);
  };

  // What is happening here, time complexity is O(n^2) because of the findIndex method.
  const generateColumns = (orderedFields, currentRows) => {
    const generatedColumns = [];

    if (isMultiSelectEnabled) {
      generatedColumns.push({
        field: "checkbox",
        headerName: "",
        width: 50,
        sortable: false,
        renderCell: (params) => {
          const rowIndex = currentRows.findIndex(
            (row) => row.id === params.row.id
          );
          return (
            <input
              type="checkbox"
              checked={selectedRows.has(params.row.id)}
              onChange={(e) => {
                e.stopPropagation();
                handleRowSelection(params.row.id, rowIndex, e);
              }}
              style={{ cursor: "pointer", width: "18px", height: "18px" }}
            />
          );
        },
      });
    }

    generatedColumns.push(
      ...orderedFields.map(([order, fieldName]) => ({
        field: fieldName,
        headerName: fieldName,
        flex: 1,
        renderCell: (params) => renderCellValue(params),
      }))
    );

    generatedColumns.push({
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Button
            sx={{ color: "white" }}
            onClick={() =>
              setEditRecordModalData({
                isOpen: true,
                data: params?.row,
              })
            }
          >
            <Pencil size={22} />
          </Button>
          <Button
            sx={{ color: "white", minWidth: 0, padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setDeleteRecordModalData({
                isOpen: true,
                data: params?.row,
              });
            }}
          >
            <Trash size={22} />
          </Button>
        </Box>
      ),
    });

    return generatedColumns;
  };

  const handleGetPageData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fget({
        url: `trackables/custom-page/${params.pageId}/data/`,
      });

      const results = res?.data?.results || [];
      const pageConfig = pageData?.page_config;
      const fieldsConfig = pageData?.fields_config;

      if (!pageConfig) {
        setIsLoading(false);
        return;
      }

      const positionMap = Object.fromEntries(
        Object.entries(pageConfig).map(([position, nameValue]) => [
          nameValue,
          Number(position),
        ])
      );
      fieldsConfig.sort((a, b) => positionMap[a.name] - positionMap[b.name]);
      setFields(fieldsConfig);

      const generatedColumns = fieldsConfig.map((field) => {
        if (field.type === "media") {
          return {
            field: field.name,
            headerName: field.name,
            flex: 1,
            renderCell: (cellParams) =>
              cellParams.value && cellParams.value.length > 0 ? (
                <Box
                  sx={{ display: "flex", gap: 1, alignItems: "center" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedMedia({
                      row: cellParams.row,
                      fieldName: field.name,
                    });
                    setIsMediaDrawerOpen(true);
                  }}
                >
                  {cellParams.value.map((item) => {
                    return (
                      <img
                        src={item}
                        alt={"image"}
                        style={{
                          width: "28px",
                          height: "28px",
                          objectFit: "cover",
                        }}
                      />
                    );
                  })}
                </Box>
              ) : (
                <IconButton
                  variant="outlined"
                  size="small"
                  sx={{
                    border: "1px dashed #606479",
                    borderRadius: "5px",
                    padding: "6px",
                    "&:hover": {
                      color: "#F9F9F9",
                      bgcolor: "rgba(255,255,255,0.06)",
                    },
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedMedia({
                      row: cellParams.row,
                      fieldName: field.name,
                    });
                    setIsMediaDrawerOpen(true);
                  }}
                >
                  <Paperclip size={18} color="#B1B5C9" />
                </IconButton>
              ),
          };
        }

        return {
          field: field.name,
          headerName: field.name,
          flex: 1,
          renderCell: (cellParams) => renderCellValue(cellParams),
        };
      });

      generatedColumns.push({
        field: "actions",
        headerName: "Actions",
        flex: 0.5,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              sx={{ color: "white !important", "&:disabled": { color: "gray !important" } }}
              disabled={pageData.fields.length===0}
              onClick={() =>
                setEditRecordModalData({
                  isOpen: true,
                  data: params?.row,
                })
              }
            >
              <Pencil size={22} />
            </Button>
            {!pageData.trackable && (
              <Button
                sx={{ color: "white", minWidth: 0, padding: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteRecordModalData({
                    isOpen: true,
                    data: params?.row,
                  });
                }}
              >
                <Trash size={22} />
              </Button>
            )}
          </Box>
        ),
      });

      setColumns(generatedColumns);

      const generatedRows = results.map((record, index) => ({
        ...record,
        id: record.id || index + 1,
      }));

      // const generatedColumns = generateColumns(orderedFields, generatedRows);

      // setColumns(generatedColumns);
      setRows(generatedRows);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.pageId, pageData]);

  const handleGetPage = useCallback(async () => {
    try {
      const res = await fget({
        url: `trackables/custom-page/${params?.pageId}/`,
      });

      if (res && res.data) {
        setPageData(res.data);
        setPageName(res.data.name);
      }
    } catch (error) {
      console.log(error);
    }
  }, [params?.pageId]);

  useEffect(() => {
    if (params?.pageId) {
      handleGetPage();
    }
  }, [params?.pageId]);

  useEffect(() => {
    if (pageData && params && params.pageId) {
      handleGetPageData();
    }
  }, [pageData, params?.pageId]);

  useEffect(() => {
    if (pageData?.page_config && rows.length > 0) {
      const orderedFields = Object.entries(pageData.page_config).sort(
        ([a], [b]) => parseInt(a) - parseInt(b)
      );
      const updatedColumns = generateColumns(orderedFields, rows);
      setColumns(updatedColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiSelectEnabled, selectedRows]);

  const handleDeleteRecord = async () => {
    const index = rows.findIndex(
      (obj) => obj.id === deleteRecordModalData?.data?.id
    );
    try {
      await fdelete({
        url: `trackables/custom-page/${params?.pageId}/update-row/${index}/`,
      });
      handleGetPageData();
      setDeleteRecordModalData({
        isOpen: false,
        data: null,
      });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div>
      <ErrorOrSuccessMessage snackValue={snackValue} />
      {!isLoading ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Link
                to={`/${params.projectId}/custom-page`}
                style={{ textDecoration: "none" }}
              >
                <Typography
                  sx={{
                    fontSize: "20px !important",
                    fontWeight: 500,
                    color: "gray",
                    cursor: "pointer",
                    "&:hover": {
                      color: "white",
                    },
                  }}
                >
                  Custom Pages
                </Typography>
              </Link>

              <CaretRight size={22} color="gray" />

              <Typography
                sx={{
                  fontSize: "20px !important",
                  fontWeight: 500,
                }}
              >
                {pageData?.name}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Button
                onClick={() => setIsCreateRecordModalOpen(true)}
                variant="outlined"
                startIcon={<Plus />}
                disabled={pageData.trackable}
                sx={{
                  textTransform: "none",
                  color: "white",
                }}
              >
                <Typography>Filter</Typography>
              </Button>
              <Button
                startIcon={<CheckSquare />}
                onClick={handleToggleMultiSelect}
                variant="text"
                sx={{
                  textTransform: "none",
                  color: "white",
                  minWidth: "0px",
                  padding: "4px 7px",
                }}
              >
                <Typography>Multi Select</Typography>
              </Button>
              <Button
                onClick={() => setIsEditPageDrawerOpen(true)}
                variant="text"
                startIcon={<Pencil />}
                sx={{
                  textTransform: "none",
                  color: "white",
                }}
              >
                <Typography>Edit</Typography>
              </Button>
              <Button
                onClick={() => {}}
                variant="contained"
                startIcon={<Plus />}
                sx={{
                  backgroundColor: "#6C65D9",
                  borderRadius: "10px",
                  textTransform: "none",
                }}
              >
                <Typography>New Record</Typography>
              </Button>
              {/* <Button
                variant="contained"
                startIcon={<Share />}
                sx={{
                  backgroundColor: "#6C65D9",
                  borderRadius: "10px",
                  textTransform: "none",
                }}
              >
                <Typography>Share</Typography>
              </Button> */}
            </Box>
          </Box>

          {columns?.length ? (
            <Box className={commonClasses.customPageTableWidth}>
              <DataGrid
                rows={rows}
                columns={columns}
                hideFooterPagination
                slots={{
                  noRowsOverlay: () => (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <NoRowsSvg />
                      <Typography
                        sx={{
                          textAlign: "center",
                          mt: 4,
                          fontSize: "22px !important",
                          color: "white",
                        }}
                      >
                        This table is currently empty. <br />
                        Check back later to view the data!
                      </Typography>
                    </Box>
                  ),
                }}
                sx={{
                  border: "none",
                  "--DataGrid-rowBorderColor": "#606479",
                  "& .MuiDataGrid-columnHeaders": {
                    color: "gray",
                    fontSize: {
                      xs: "12px",
                      sm: "14px",
                      md: "14px",
                      lg: "16px",
                      xl: "16px",
                    },
                  },
                  "& .MuiDataGrid-columnHeader": {
                    color: "gray",
                    borderBottom: "1px solid #606479",
                  },
                  "& .MuiDataGrid-withBorderColor": {
                    border: "none !important",
                  },
                  "& .MuiDataGrid-cell": {
                    color: "white",
                    display: "flex",
                    justifyContent: "space-between",
                    borderColor: "#606479",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-row": {
                    borderBottom: "none",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#252A38",
                    },
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    marginBottom: 0,
                  },
                  "& .MuiDataGrid-footerContainer": {
                    display: "none",
                  },
                  "& .MuiDataGrid-main": {
                    paddingBottom: "0px",
                  },
                  "& .MuiDataGrid-container--top [role='row'], & .MuiDataGrid-container--bottom [role='row']":
                    {
                      backgroundColor: "#1B1D28 !important",
                      borderColor: "gray",
                    },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-columnHeader:focus": {
                    outline: "none",
                  },
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <NoRowsSvg />
              <Typography
                sx={{ textAlign: "center", mt: 4, fontSize: "22px !important" }}
              >
                This table is currently empty. <br />
                Create new records to get started!
              </Typography>
            </Box>
          )}

          {/* <Modal
            open={isCreateRecordModalOpen}
            onClose={() => setIsCreateRecordModalOpen(false)}
            className={commonClasses?.flexCenter}
          >
            <CustomPageEntryOrEditRecord
              type="new"
              fields={fields}
              pageData={pageData}
              pageId={params?.pageId}
              handleClose={() => setIsCreateRecordModalOpen(false)}
              handleRefresh={() => handleGetPageData()}
              setSnackValue={setSnackValue}
            />
          </Modal> */}
          <Modal
            open={editRecordModalData?.isOpen}
            onClose={() =>
              setEditRecordModalData({
                isOpen: false,
                data: null,
              })
            }
            className={commonClasses?.flexCenter}
          >
            <CustomPageEntryOrEditRecord
              type="edit"
              pageData={pageData}
              editData={editRecordModalData?.data}
              fields={fields}
              pageId={params?.pageId}
              handleClose={() =>
                setEditRecordModalData({
                  isOpen: false,
                  data: null,
                })
              }
              handleRefresh={() => handleGetPageData()}
              rows={rows}
              setSnackValue={setSnackValue}
            />
          </Modal>
          {isMediaDrawerOpen && (
            <MediaDrawer
              open={isMediaDrawerOpen}
              onClose={() => setIsMediaDrawerOpen(false)}
              type="custompage"
              pageId={params?.pageId}
              selectedMedia={selectedMedia}
              fieldIdentifier={
                fields.find((field) => field.name === selectedMedia.fieldName)
                  ?.identifier
              }
              handleRefresh={() => handleGetPageData()}
            />
          )}
          <Modal
            open={deleteRecordModalData?.isOpen}
            onClose={() =>
              setDeleteRecordModalData({
                isOpen: false,
                data: null,
              })
            }
            className={commonClasses?.flexCenter}
          >
            <DeleteModal
              type="Record"
              name={deleteRecordModalData?.data?.index}
              handleCloseDeleteModal={() =>
                setDeleteRecordModalData({
                  isOpen: false,
                  data: null,
                })
              }
              handleDelete={handleDeleteRecord}
            />
          </Modal>
          <Drawer
            open={isEditPageDrawerOpen}
            onClose={() => setIsEditPageDrawerOpen(false)}
            anchor="right"
            slotProps={{
              backdrop: {
                sx: {
                  backgroundColor: "rgba(0, 0, 0, 0.22)",
                  top: "65px",
                  "@media (min-width: 1280px)": {
                    top: "80px",
                  },
                  opacity: 0,
                },
              },
              paper: {
                sx: {
                  width: { xs: "100%", sm: 600 },
                  maxWidth: "100vw",
                  bgcolor: "#1B1D28",
                  color: "#F9F9F9",
                  borderLeft: "1px solid #606479",
                  borderTop: "1px solid #606479",
                  boxSizing: "border-box",
                  top: "65px",
                  "@media (min-width: 1280px)": {
                    top: "80px",
                  },
                  bottom: 0,
                  height: "auto",
                },
              },
            }}
          >
            <EditCustomPageModal
              handleClose={() => setIsEditPageDrawerOpen(false)}
              projectId={params.projectId}
              pageData={pageData}
              onSuccess={() => {
                handleGetPage();
                handleGetPageData();
              }}
            />
          </Drawer>
        </>
      ) : (
        <Loader />
      )}
    </div>
  );
};

export default CustomPageRendererOverview;
